import { detectPrimaryObjectBox } from './visualSearchObjectDetector'

const CROP_PADDING_RATIO = 0.1
const MAX_IMAGE_SIDE = 1024
const JPEG_QUALITY = 0.85
/** Covers first-load WASM+model download; later calls are much faster. */
const PREPARE_TIMEOUT_MS = 12000
const DEFAULT_SCORE_THRESHOLD = 0.4

export type PreparedVisualSearchImage = {
    imageB64: string
    /** Etiqueta COCO en inglés; null si no hubo detección confiable. */
    objectLabel: string | null
    objectConfidence: number | null
}

const emptyPrepared = (imageB64: string): PreparedVisualSearchImage => ({
    imageB64,
    objectLabel: null,
    objectConfidence: null
})

const isObjectCropEnabled = (): boolean => {
    const flag = process.env.NEXT_PUBLIC_VISUAL_SEARCH_OBJECT_CROP
    if (flag === undefined || flag === '') {
        return true
    }
    return flag === 'true' || flag === '1'
}

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> => {
    return new Promise((resolve, reject) => {
        const timer = window.setTimeout(() => {
            reject(new Error(`prepareVisualSearchImage timeout after ${ms}ms`))
        }, ms)

        promise
            .then((value) => {
                window.clearTimeout(timer)
                resolve(value)
            })
            .catch((error) => {
                window.clearTimeout(timer)
                reject(error)
            })
    })
}

const loadImageFromFile = (file: File): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const image = new Image()
        image.onload = () => {
            URL.revokeObjectURL(url)
            resolve(image)
        }
        image.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error('Could not decode image for visual search'))
        }
        image.src = url
    })
}

const clamp = (value: number, min: number, max: number): number =>
    Math.min(max, Math.max(min, value))

const expandBoxWithPadding = (
    box: { originX: number; originY: number; width: number; height: number },
    imageWidth: number,
    imageHeight: number,
    paddingRatio: number
) => {
    const padX = box.width * paddingRatio
    const padY = box.height * paddingRatio
    const left = clamp(Math.floor(box.originX - padX), 0, imageWidth - 1)
    const top = clamp(Math.floor(box.originY - padY), 0, imageHeight - 1)
    const right = clamp(
        Math.ceil(box.originX + box.width + padX),
        left + 1,
        imageWidth
    )
    const bottom = clamp(
        Math.ceil(box.originY + box.height + padY),
        top + 1,
        imageHeight
    )

    return {
        sx: left,
        sy: top,
        sw: right - left,
        sh: bottom - top
    }
}

const drawToCanvas = (
    source: CanvasImageSource,
    sx: number,
    sy: number,
    sw: number,
    sh: number
): HTMLCanvasElement => {
    let targetW = sw
    let targetH = sh
    const maxSide = Math.max(sw, sh)

    if (maxSide > MAX_IMAGE_SIDE) {
        const scale = MAX_IMAGE_SIDE / maxSide
        targetW = Math.max(1, Math.round(sw * scale))
        targetH = Math.max(1, Math.round(sh * scale))
    }

    const canvas = document.createElement('canvas')
    canvas.width = targetW
    canvas.height = targetH
    const ctx = canvas.getContext('2d')
    if (!ctx) {
        throw new Error('Canvas 2D context unavailable')
    }
    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, targetW, targetH)
    return canvas
}

const canvasToBase64 = (canvas: HTMLCanvasElement): string => {
    const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY)
    const parts = dataUrl.split(',')
    return parts[1] ?? ''
}

const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
            const result = reader.result
            if (typeof result !== 'string') {
                reject(new Error('FileReader did not return a data URL'))
                return
            }
            const base64 = result.split(',')[1]
            if (!base64) {
                reject(new Error('Empty base64 payload'))
                return
            }
            resolve(base64)
        }
        reader.onerror = () =>
            reject(reader.error ?? new Error('FileReader error'))
        reader.readAsDataURL(file)
    })
}

const prepareWithCrop = async (
    file: File
): Promise<PreparedVisualSearchImage> => {
    const image = await loadImageFromFile(file)
    const primary = await detectPrimaryObjectBox(image, DEFAULT_SCORE_THRESHOLD)

    if (!primary) {
        const canvas = drawToCanvas(
            image,
            0,
            0,
            image.naturalWidth,
            image.naturalHeight
        )
        return emptyPrepared(canvasToBase64(canvas))
    }

    const crop = expandBoxWithPadding(
        primary,
        image.naturalWidth,
        image.naturalHeight,
        CROP_PADDING_RATIO
    )
    const canvas = drawToCanvas(image, crop.sx, crop.sy, crop.sw, crop.sh)
    const label =
        primary.categoryName && primary.score >= DEFAULT_SCORE_THRESHOLD
            ? primary.categoryName
            : null

    return {
        imageB64: canvasToBase64(canvas),
        objectLabel: label,
        objectConfidence: primary.score
    }
}

/**
 * Prepares a user photo for getVisualSearch:
 * - Optional MediaPipe object crop (feature flag)
 * - Resize + JPEG compress
 * - Returns base64 + optional COCO object label for server-side fallback
 *
 * Falls back to full-image base64 on any failure / timeout / flag off.
 */
export const prepareVisualSearchImage = async (
    file: File
): Promise<PreparedVisualSearchImage> => {
    if (typeof window === 'undefined') {
        return emptyPrepared(await fileToBase64(file))
    }

    if (!isObjectCropEnabled()) {
        try {
            const image = await loadImageFromFile(file)
            const canvas = drawToCanvas(
                image,
                0,
                0,
                image.naturalWidth,
                image.naturalHeight
            )
            return emptyPrepared(canvasToBase64(canvas))
        } catch {
            return emptyPrepared(await fileToBase64(file))
        }
    }

    try {
        return await withTimeout(prepareWithCrop(file), PREPARE_TIMEOUT_MS)
    } catch (error) {
        console.warn(
            '[visual-search] crop prepare failed, falling back to full image:',
            error
        )
        return emptyPrepared(await fileToBase64(file))
    }
}
